import React, { useMemo, useState } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import Container from "@material-ui/core/Container";
import FormLabel from "@material-ui/core/FormLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
// @material-ui/icons components
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import Search from "@material-ui/icons/Search";
// core components
import Snackbar from "components/Snackbar/Snackbar.js";
import componentStyles from "assets/theme/views/admin/react-bs-table.js";
import btnComponentStyles from "assets/theme/components/button.js";
import LoadingAnimation from "components/Animations/loading";
import { useAsyncEffect } from "use-async-effect";
import { redirectToLogout } from "common/SharedFn";
import {
  getAllAdminParams,
  addAdminParam,
  updateAdminParam,
} from "../../network/ApiAxios";
import {
  BOOL_OFF,
  BOOL_ON,
  CONFIG_GROUPS,
  CONFIG_SCHEMA,
  findMissingKeys,
  groupConfigRows,
  isBoolOn,
  pairConfigRows,
  sectionConfigRows,
  validateConfigValue,
} from "./configSchema";

const useStyles = makeStyles(componentStyles);
const useStylesButtons = makeStyles(btnComponentStyles);

// The Argon theme turns every MuiTab into a filled, shadowed pill and hides the
// indicator entirely (assets/theme/theme.js). These overrides put back a plain
// underline tab bar, which is what a settings page wants.
const useTabStyles = makeStyles({
  tabsRoot: {
    minHeight: 48,
  },
  tabsScroller: {
    padding: "0!important",
  },
  tabsIndicator: {
    display: "block!important",
    height: 2,
    backgroundColor: "#5e72e4",
  },
  // A scroll button that has nothing to scroll to is disabled but still holds
  // its 40px slot, which indents the first tab away from the panel edge.
  // Collapse it so the arrow only takes room when it can actually be used.
  tabsScrollButtons: {
    width: 28,
    transition: "width .15s ease",
    "&.Mui-disabled": {
      width: 0,
      opacity: 0,
    },
  },
  tabRoot: {
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    color: "#8898aa",
    backgroundColor: "transparent!important",
    boxShadow: "none!important",
    borderRadius: "0!important",
    padding: "0 2px!important",
    marginRight: "2rem!important",
    minWidth: "auto!important",
    minHeight: 48,
    maxWidth: "none",
    flex: "0 0 auto!important",
    opacity: 1,
    transition: "color .15s ease",
    "&:hover": {
      color: "#172b4d",
    },
    "&$tabSelected": {
      color: "#5e72e4!important",
      backgroundColor: "transparent!important",
      fontWeight: 600,
    },
  },
  tabSelected: {},
});

const UNKNOWN_GROUP = {
  id: "__unknown",
  label: "Other",
  description:
    "Stored in the database but not yet part of the dashboard. Edit these only if you know what they do.",
};

const Badge = ({ label, tone }) => {
  const tones = {
    info: { color: "#5e72e4", backgroundColor: "#eef0fd" },
    danger: { color: "#f5365c", backgroundColor: "#fdeef1" },
  };
  return (
    <Box
      component="span"
      style={{
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 10,
        padding: "1px 7px",
        whiteSpace: "nowrap",
        ...tones[tone],
      }}
    >
      {label}
    </Box>
  );
};

const AdminParamsTable = () => {
  const classes = { ...useStyles(), ...useStylesButtons() };
  const tabClasses = useTabStyles();
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(CONFIG_GROUPS[0].id);
  const [notifMessage, setNotifMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);

  const showSuccessNotification = (message) => {
    setNotifMessage(message);
    setSuccess(true);
    setTimeout(function () {
      setSuccess(false);
    }, 3000);
  };

  const showErrorNotification = (message) => {
    setNotifMessage(message);
    setShowError(true);
    setTimeout(function () {
      setShowError(false);
    }, 3000);
  };

  const fetchAdminParams = async () => {
    setForceRefresh(false);
    setIsLoading(true);
    try {
      const response = await getAllAdminParams();
      const { data } = response;
      if (data.result.isSuccess) {
        setRows(data.data ?? []);
        setEdits({});
        setErrors({});
      } else {
        showErrorNotification(data.result.description);
        if (data.result.code == -2) {
          redirectToLogout();
        }
      }
    } catch (ex) {
      showErrorNotification("Failed to fetch settings");
    }
    setIsLoading(false);
  };

  useAsyncEffect(async () => {
    fetchAdminParams();
  }, [forceRefresh]);

  // Rows the API returned, plus placeholders for schema keys that have no row
  // yet. A missing key breaks the app at runtime, so they are surfaced here
  // rather than hidden.
  const allRows = useMemo(() => {
    const missing = findMissingKeys(rows).map((paramKey) => ({
      id: null,
      paramKey,
      paramName: "",
      paramValue: "",
      isMissing: true,
    }));
    return [...rows, ...missing];
  }, [rows]);

  const { grouped, unknown } = useMemo(
    () => groupConfigRows(allRows),
    [allRows]
  );

  const tabs = useMemo(() => {
    const list = CONFIG_GROUPS.filter(
      (group) => (grouped[group.id] ?? []).length > 0
    );
    if (unknown.length > 0) list.push(UNKNOWN_GROUP);
    return list;
  }, [grouped, unknown]);

  const rowsForGroup = (groupId) =>
    groupId === UNKNOWN_GROUP.id ? unknown : grouped[groupId] ?? [];

  const getValue = (row) =>
    edits[row.paramKey] !== undefined
      ? edits[row.paramKey]
      : row.paramValue ?? "";

  const isDirty = (row) => edits[row.paramKey] !== undefined;

  const handleChange = (row, value) => {
    const schema = CONFIG_SCHEMA[row.paramKey];
    setEdits((current) => {
      const next = { ...current };
      // Typing the original value back is not a change, unless the row is a
      // placeholder that still has to be created.
      if (!row.isMissing && value === (row.paramValue ?? "")) {
        delete next[row.paramKey];
      } else {
        next[row.paramKey] = value;
      }
      return next;
    });
    setErrors((current) => {
      const next = { ...current };
      const message = schema ? validateConfigValue(schema, value) : null;
      if (message) {
        next[row.paramKey] = message;
      } else {
        delete next[row.paramKey];
      }
      return next;
    });
  };

  const searchTerm = search.trim().toLowerCase();

  const matchesSearch = (row) => {
    if (!searchTerm) return true;
    const schema = CONFIG_SCHEMA[row.paramKey];
    return (
      (schema?.label ?? row.paramKey).toLowerCase().includes(searchTerm) ||
      (schema?.section ?? "").toLowerCase().includes(searchTerm) ||
      (row.paramName ?? "").toLowerCase().includes(searchTerm) ||
      (schema?.help ?? "").toLowerCase().includes(searchTerm)
    );
  };

  const dirtyKeys = Object.keys(edits);
  const errorKeys = Object.keys(errors);

  const dirtyCountForGroup = (groupId) =>
    rowsForGroup(groupId).filter(isDirty).length;

  const saveChanges = async () => {
    // Re-validate everything pending: a value can be dirty without having been
    // touched since the last error was cleared.
    const pendingErrors = {};
    dirtyKeys.forEach((key) => {
      const schema = CONFIG_SCHEMA[key];
      if (!schema) return;
      const message = validateConfigValue(schema, edits[key]);
      if (message) pendingErrors[key] = message;
    });
    if (Object.keys(pendingErrors).length > 0) {
      setErrors(pendingErrors);
      showErrorNotification("Fix the highlighted settings before saving");
      return;
    }

    setIsSaving(true);
    const failed = [];
    for (const key of dirtyKeys) {
      const row = allRows.find((item) => item.paramKey === key);
      const schema = CONFIG_SCHEMA[key];
      const label = schema?.label ?? key;
      // The backend rejects a blank paramName outright, and rows for settings
      // that don't exist yet have nothing to send. Fall back to the schema
      // label so a first-time save carries a real description.
      const paramName = row.paramName || label;
      try {
        const response =
          row.id == null
            ? await addAdminParam(key, paramName, edits[key])
            : await updateAdminParam(row.id, key, paramName, edits[key]);
        if (!response.data.result.isSuccess) {
          failed.push(label);
          if (response.data.result.code == -2) {
            redirectToLogout();
            return;
          }
        }
      } catch (ex) {
        failed.push(label);
      }
    }
    setIsSaving(false);

    if (failed.length === 0) {
      showSuccessNotification(
        dirtyKeys.length === 1
          ? "Setting saved"
          : `${dirtyKeys.length} settings saved`
      );
    } else {
      showErrorNotification(`Failed to save: ${failed.join(", ")}`);
    }
    setForceRefresh(true);
  };

  const discardChanges = () => {
    setEdits({});
    setErrors({});
  };

  const renderControl = (row) => {
    const schema = CONFIG_SCHEMA[row.paramKey];
    const value = getValue(row);
    const error = errors[row.paramKey];

    let control;
    if (schema?.type === "boolean") {
      control = (
        <Switch
          color="primary"
          checked={isBoolOn(value)}
          onChange={(e) =>
            handleChange(row, e.target.checked ? BOOL_ON : BOOL_OFF)
          }
        />
      );
    } else if (schema?.type === "select") {
      control = (
        <Select
          fullWidth
          variant="outlined"
          value={value}
          IconComponent={KeyboardArrowDown}
          onChange={(e) => handleChange(row, String(e.target.value))}
        >
          {schema.options.map((option) => (
            <MenuItem key={option.value} value={String(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    } else if (schema?.type === "textarea") {
      control = (
        <OutlinedInput
          fullWidth
          multiline
          rows={3}
          value={value}
          error={Boolean(error)}
          onChange={(e) => handleChange(row, e.target.value)}
        />
      );
    } else {
      const numeric =
        schema?.type === "integer" || schema?.type === "decimal";
      control = (
        <OutlinedInput
          fullWidth
          type={numeric ? "number" : "text"}
          value={value}
          error={Boolean(error)}
          inputProps={
            numeric
              ? {
                  min: schema.min,
                  max: schema.max,
                  step: schema.step ?? (schema.type === "integer" ? 1 : "any"),
                }
              : {}
          }
          endAdornment={
            schema?.unit ? (
              <InputAdornment position="end">
                <Typography variant="body2" style={{ color: "#8898aa" }}>
                  {schema.unit}
                </Typography>
              </InputAdornment>
            ) : null
          }
          onChange={(e) => handleChange(row, e.target.value)}
        />
      );
    }

    return control;
  };

  const rowStyle = {
    padding: "14px 0",
    borderBottom: "1px solid #f1f3f7",
  };

  const renderBadges = (row) => (
    <>
      {isDirty(row) && <Badge label="Unsaved" tone="info" />}
      {row.isMissing && (
        <Tooltip title="This setting has no value yet. The app may fail until it is set.">
          <span>
            <Badge label="Not set" tone="danger" />
          </span>
        </Tooltip>
      )}
    </>
  );

  const renderField = (row) => {
    const schema = CONFIG_SCHEMA[row.paramKey];
    const error = errors[row.paramKey];
    const control = renderControl(row);

    // Settings with no schema entry fall back to a plain text box.
    const label = schema?.label ?? row.paramKey;
    // The DB description is the client's own wording, so it stays as the hint.
    const hint = schema?.help || row.paramName || "";
    // Long free text needs the full width, so it sits under the label instead
    // of beside it.
    const isStacked = schema?.type === "textarea";

    // Controls are sized to the value they hold - a full-width box for a
    // two-digit number is what made this page feel noisy.
    const controlWidth =
      schema?.type === "boolean"
        ? "auto"
        : schema?.type === "select"
        ? 220
        : schema?.type === "integer" || schema?.type === "decimal"
        ? schema?.unit
          ? 220
          : 140
        : 320;

    const description = (
      <>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <FormLabel
            style={{
              marginBottom: 0,
              fontSize: 14,
              fontWeight: 600,
              color: "#32325d",
            }}
          >
            {label}
          </FormLabel>
          {renderBadges(row)}
        </Box>
        {(error || hint) && (
          <FormHelperText
            error={Boolean(error)}
            style={{ marginTop: 2, fontSize: 13 }}
          >
            {error || hint}
          </FormHelperText>
        )}
      </>
    );

    // Multi-line text stacks: label above, full-width box below. Kept as a
    // plain block - a column flex container would read `flex-basis` as a
    // height and leave a large gap under the label.
    if (isStacked) {
      return (
        <Box key={row.paramKey} style={rowStyle}>
          {description}
          <Box marginTop="0.5rem">{control}</Box>
        </Box>
      );
    }

    return (
      <Box
        key={row.paramKey}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        style={{ ...rowStyle, gap: 16 }}
      >
        <Box flex="1 1 320px" minWidth={0}>
          {description}
        </Box>
        <Box
          flex="0 0 auto"
          width={controlWidth}
          display="flex"
          justifyContent="flex-end"
        >
          {control}
        </Box>
      </Box>
    );
  };

  // A notification is one message, not two settings, so its title and body sit
  // in a single block under a shared heading.
  const renderPair = (item) => {
    const parts = [
      { caption: "Title", row: item.title },
      { caption: "Message", row: item.message },
    ].filter((part) => part.row);

    return (
      <Box key={item.key} style={rowStyle}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <FormLabel
            style={{
              marginBottom: 0,
              fontSize: 14,
              fontWeight: 600,
              color: "#32325d",
            }}
          >
            {item.label}
          </FormLabel>
          {parts.map((part) => (
            <React.Fragment key={part.caption}>
              {renderBadges(part.row)}
            </React.Fragment>
          ))}
        </Box>
        <Box marginTop="0.75rem">
          {parts.map((part) => {
            const error = errors[part.row.paramKey];
            return (
              <Box key={part.caption} marginBottom="0.75rem">
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#8898aa",
                    marginBottom: 4,
                  }}
                >
                  {part.caption}
                </Typography>
                {renderControl(part.row)}
                {error && (
                  <FormHelperText error style={{ fontSize: 13 }}>
                    {error}
                  </FormHelperText>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderItem = (item) =>
    item.kind === "pair" ? renderPair(item) : renderField(item.row);

  const renderSections = (group, groupRows) => {
    const sections =
      group.id === UNKNOWN_GROUP.id
        ? [{ section: { label: "" }, rows: groupRows }]
        : sectionConfigRows(group, groupRows);

    return sections.map(({ section, rows: sectionRows }, index) => (
      <Box key={section.label || index} marginBottom="2.5rem">
        {section.label && (
          <Box marginBottom="0.25rem">
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#8898aa",
              }}
            >
              {section.label}
            </Typography>
            {section.description && (
              <Typography
                variant="body2"
                style={{ color: "#8898aa", marginTop: 2 }}
              >
                {section.description}
              </Typography>
            )}
          </Box>
        )}
        <Box>{pairConfigRows(sectionRows).map(renderItem)}</Box>
      </Box>
    ));
  };

  // While searching, matches from every tab are shown together - hunting for a
  // setting is the one time the tab boundaries get in the way.
  const searchResults = !searchTerm
    ? []
    : tabs
        .map((group) => ({
          group,
          rows: rowsForGroup(group.id).filter(matchesSearch),
        }))
        .filter((entry) => entry.rows.length > 0);

  const activeGroup =
    tabs.find((group) => group.id === activeTab) ?? tabs[0] ?? null;

  return (
    <Container
      maxWidth={false}
      component={Box}
      marginTop="-4.5rem"
      classes={{ root: classes.containerRoot }}
    >
      <Card elevation={0} style={{ marginBottom: 24 }}>
        <CardHeader
          style={{ paddingTop: "0.875rem", paddingBottom: "0.875rem" }}
          title={
            <Box component={Typography} variant="h2" marginBottom="0!important">
              System Settings
            </Box>
          }
          action={
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              justifyContent="flex-end"
              style={{ gap: 8, marginTop: 8, marginRight: 8 }}
            >
              <OutlinedInput
                placeholder="Search settings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ height: 40, minWidth: 240, backgroundColor: "#fff" }}
                startAdornment={
                  <InputAdornment position="start">
                    <Search
                      style={{ width: 18, height: 18, color: "#8898aa" }}
                    />
                  </InputAdornment>
                }
              />
              {dirtyKeys.length > 0 && (
                <Button onClick={discardChanges} disabled={isSaving}>
                  Discard
                </Button>
              )}
              <Button
                classes={{ root: classes.buttonContainedPurple100 }}
                variant="contained"
                disabled={
                  dirtyKeys.length === 0 || errorKeys.length > 0 || isSaving
                }
                onClick={saveChanges}
              >
                {isSaving
                  ? "Saving..."
                  : dirtyKeys.length > 0
                  ? `Save ${dirtyKeys.length} change${
                      dirtyKeys.length === 1 ? "" : "s"
                    }`
                  : "Save"}
              </Button>
            </Box>
          }
        />
      </Card>

      {isLoading ? (
        <Box display="flex" justifyContent="center">
          <div className="text-center" style={{ width: "20%" }}>
            <LoadingAnimation />
          </div>
        </Box>
      ) : searchTerm ? (
        <Card elevation={0}>
          <CardContent>
            {searchResults.length === 0 ? (
              <Typography variant="body2" style={{ color: "#8898aa" }}>
                No settings match "{search}".
              </Typography>
            ) : (
              searchResults.map(({ group, rows: resultRows }) => (
                <Box key={group.id} marginBottom="2.5rem">
                  <Typography
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#8898aa",
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Box>{pairConfigRows(resultRows).map(renderItem)}</Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      ) : (
        activeGroup && (
          <Card elevation={0}>
            <Box
              style={{
                borderBottom: "1px solid #e9ecef",
                padding: "0 1.5rem",
              }}
            >
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                value={activeGroup.id}
                onChange={(e, value) => setActiveTab(value)}
                classes={{
                  root: tabClasses.tabsRoot,
                  scroller: tabClasses.tabsScroller,
                  indicator: tabClasses.tabsIndicator,
                  scrollButtons: tabClasses.tabsScrollButtons,
                }}
              >
                {tabs.map((group) => {
                  const dirtyCount = dirtyCountForGroup(group.id);
                  return (
                    <Tab
                      key={group.id}
                      value={group.id}
                      disableRipple
                      classes={{
                        root: tabClasses.tabRoot,
                        selected: tabClasses.tabSelected,
                      }}
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 8 }}
                        >
                          <span>{group.label}</span>
                          {dirtyCount > 0 && (
                            <Badge label={dirtyCount} tone="info" />
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            <CardContent>
              <Box marginBottom="1.5rem">
                <Typography variant="body2" style={{ color: "#8898aa" }}>
                  {activeGroup.description}
                </Typography>
              </Box>
              {renderSections(activeGroup, rowsForGroup(activeGroup.id))}
            </CardContent>
          </Card>
        )
      )}

      <Snackbar
        place="tr"
        color="success"
        message={notifMessage}
        open={success}
        closeNotification={() => setSuccess(false)}
        close
      />

      <Snackbar
        place="tr"
        color="rose"
        message={notifMessage}
        open={showError}
        closeNotification={() => setShowError(false)}
        close
      />
    </Container>
  );
};

export default AdminParamsTable;
